import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, EnvelopeSimple, Lock } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";

/**
 * Two-step password reset flow:
 *   1. No `token` query param → email-request form (`POST /api/auth/forgot-password`)
 *   2. `?token=…` in URL      → new-password form (`POST /api/auth/reset-password`)
 *
 * The backend endpoints already exist — this page just wires the UI to them.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("If that email exists, a reset link has been sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset — you can sign in now");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed — the link may have expired");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-zinc-50 p-6">
      <div className="w-full max-w-md bg-white border border-zinc-950" data-testid="reset-password-page">
        <div className="border-b border-zinc-950 px-6 py-4 bg-zinc-950 text-white">
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#FF3E00]">SMARTERP / ACCOUNT RECOVERY</div>
          <div className="font-display text-2xl font-black tracking-tight mt-1">
            {token ? "Set a new password." : "Reset password."}
          </div>
        </div>

        <div className="p-6">
          {token ? (
            <form onSubmit={confirmReset} className="space-y-5" data-testid="reset-confirm-form">
              <p className="text-sm text-zinc-600">Choose a new password for your SmartERP account.</p>
              <div>
                <label className="label-sm text-zinc-500">New password</label>
                <div className="flex items-center gap-3 border-b border-zinc-900/20 focus-within:border-zinc-950 py-2">
                  <Lock size={16} className="text-zinc-400" />
                  <input
                    data-testid="reset-new-password-input"
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    minLength={8}
                    className="flex-1 bg-transparent outline-none text-sm"
                    placeholder="At least 8 characters"
                  />
                </div>
              </div>
              <div>
                <label className="label-sm text-zinc-500">Confirm new password</label>
                <div className="flex items-center gap-3 border-b border-zinc-900/20 focus-within:border-zinc-950 py-2">
                  <Lock size={16} className="text-zinc-400" />
                  <input
                    data-testid="reset-confirm-password-input"
                    type="password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    required
                    minLength={8}
                    className="flex-1 bg-transparent outline-none text-sm"
                  />
                </div>
              </div>
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? "SAVING…" : "SET PASSWORD"}
                {!loading && <ArrowRight size={14} weight="bold" />}
              </button>
            </form>
          ) : sent ? (
            <div className="space-y-4" data-testid="reset-request-sent">
              <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
                If <b>{email}</b> is on file, you'll receive a reset link within a minute. Check spam if it doesn't show up.
              </div>
              <Link to="/login" className="text-xs font-mono text-zinc-700 underline hover:text-[#FF3E00]" data-testid="reset-back-to-login">
                ← BACK TO SIGN IN
              </Link>
            </div>
          ) : (
            <form onSubmit={requestReset} className="space-y-5" data-testid="reset-request-form">
              <p className="text-sm text-zinc-600">Enter your account email — we'll send you a secure reset link.</p>
              <div>
                <label className="label-sm text-zinc-500">Email</label>
                <div className="flex items-center gap-3 border-b border-zinc-900/20 focus-within:border-zinc-950 py-2">
                  <EnvelopeSimple size={16} className="text-zinc-400" />
                  <input
                    data-testid="reset-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 bg-transparent outline-none text-sm"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <button
                data-testid="reset-request-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? "SENDING…" : "SEND RESET LINK"}
                {!loading && <ArrowRight size={14} weight="bold" />}
              </button>
              <Link to="/login" className="text-xs font-mono text-zinc-700 underline hover:text-[#FF3E00] block" data-testid="reset-back-to-login">
                ← BACK TO SIGN IN
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
